import { Controller, FieldError, useFormContext } from "react-hook-form";
import { FormHelperText, InputAdornment, TextField } from "@mui/material";
import { useMemo, useState } from "react";
import { NumericFormat } from "react-number-format";
import { IInputNumber } from "components/Input/InputNumber/InputNumberProps";
import { FnComponents, I18n, formatNumber } from "utilities/utilities";
import { I18NextNs, TypeComponent } from "enum/enum";


const InputReactNumber = (props: IInputNumber) => {
    const {
        disabled = false,
        defaultValue = null,
        id,
        IsShowMessageError = true,
        placeholder,
        textAlign = "right",
        variant = "outlined",
        label,
        max = null,
        maxDigits = 2,
        maxLength,
        min = null,
        name,
        required = false,
        size = "small",
        valueType = "number",
        IsCanMinus = false,
    } = props;

    const [isShrink, setIsShrink] = useState(false);
    const { control, register } = useFormContext();

    const rules = useMemo(() => {

        if (disabled) { return {} }
        let objvalidate = {};
        objvalidate["required"] = { value: required };
        objvalidate["validate"] = {
            value: (value) => {
                if (!value) { return true; }
                if (min && max) {
                    return value >= min && value <= max;
                }
                else if (min) {
                    return value >= min;
                }
                else if (max) {
                    return value <= max;
                }
            }
        };
        return objvalidate;
    }, [disabled, max, min, required]);

    const getMessageValidate = (error: FieldError) => {
        let sMessage = "";
        if (IsShowMessageError) {
            if (error) {
                switch (error.type) {
                    case "required":
                        sMessage = `${I18n.SetText("required", I18NextNs.validation)} ${label}`;
                        break;
                    case "pattern":
                        sMessage = `${I18n.SetText("patternNumber", I18NextNs.validation)}`;
                        break;
                    case "validate":
                        sMessage = genMessage(min, max);
                        break;
                }
            }
        }
        return sMessage;
    };

    const genMessage = (min, max) => {
        let sMessage = "";
        if (min && max) {
            sMessage = `${I18n.SetText("inputMinMax", I18NextNs.validation)} ${min} ${I18n.SetText("to")} ${max} `;
        }
        else if (min && !max) {
            sMessage = `${I18n.SetText("inputMin", I18NextNs.validation)} ${min}`;
        }
        else if (!min && max) {
            let sMin = formatNumber(max, maxDigits);
            sMessage = `${I18n.SetText("inputMax", I18NextNs.validation)} ${sMin} `;
        }
        return sMessage;
    }

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            // shouldUnregister={true}
            defaultValue={defaultValue}
            render={({
                field: { onChange, value, ref },
                fieldState: { invalid, error },
            }) => {

                let IsValid = invalid && !disabled;
                let helperText = getMessageValidate(error);
                const shrink = !!value || value === 0 || isShrink || !!props.startAdornment || !!props.endAdornment;

                return (
                    <>
                        <TextField
                            {...register(name)}
                            required={required}
                            // inputRef={ref}
                            type={"text"}
                            name={name}
                            // id={FnComponents.GetId(TypeComponent.inputNumber, id, name)}
                            fullWidth
                            value={value ?? ""}
                            error={IsValid}
                            variant={variant}
                            size={size}
                            placeholder={placeholder}
                            label={label}
                            disabled={disabled}
                            autoComplete="off"
                            inputProps={{
                                style: {
                                    textAlign: textAlign,
                                },
                                config: {
                                    disabled: disabled,
                                    decimalScale: maxDigits,
                                    valueType: valueType,
                                    IsCanMinus : IsCanMinus
                                },
                                maxLength: maxLength,
                            }}
                            InputProps={{
                                inputComponent: NumberFormatCustom,
                                startAdornment: props.startAdornment ? (
                                    <InputAdornment position="start">
                                        {props.startAdornment}
                                    </InputAdornment>
                                ) : null,
                                endAdornment: props.endAdornment ? (
                                    <InputAdornment position="end">
                                        {props.endAdornment}
                                    </InputAdornment>
                                ) : null,
                            }}
                            InputLabelProps={{
                                shrink: shrink,
                            }}
                            onChange={(e) => {
                                onChange(e.target.value);
                                if (props.onChange) {
                                    props.onChange(e.target.value, e);
                                }
                            }}
                            onBlur={(e) => {
                                setIsShrink(false);
                                if (props.onBlur) {
                                    props.onBlur(e.target.value, e);
                                }
                            }}
                            onFocus={() => {
                                setIsShrink(true);
                            }}
                        />
                        {IsShowMessageError && IsValid && (
                            <FormHelperText>{helperText}</FormHelperText>
                        )}
                    </>
                )
            }}
        />
    );
};

export default InputReactNumber

function NumberFormatCustom(props) {
    const { inputRef, onChange, disabled, config, ...other } = props;
    return (
        <NumericFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        value: config.valueType === "number" ? values.floatValue : values.value,
                    },
                });
            }}
            disabled={config.disabled}
            fixedDecimalScale
            decimalScale={config.decimalScale}
            allowNegative={config.IsCanMinus || false}
            thousandSeparator={true}
        />
    );
}

